export const WINDOW_MS = 32;
/** Today's values — identical to the old `SILERO_SHADOW_PARAMS`, with the floor off. */
export const DEFAULT_BARGE_IN_CONFIG = {
    probability: 0.5,
    onset_ms: 288,
    abandon_ms: 64,
    max_window_ms: 896,
    min_level_dbfs: -100,
};
export const BARGE_IN_BOUNDS = {
    probability: [0.1, 0.99],
    onset_ms: [96, 2000],
    abandon_ms: [32, 500],
    max_window_ms: [224, 6000],
    min_level_dbfs: [-100, -10],
};
// Like the backend's float(): null / empty / non-numeric means "not set" (the default),
// never 0 — Number(null) would otherwise quietly turn an unset value into the minimum.
const num = (value, fallback) => {
    if (value === null || value === undefined || typeof value === 'boolean')
        return fallback;
    const n = typeof value === 'string' && value.trim() === '' ? NaN : Number(value);
    return Number.isFinite(n) ? n : fallback;
};
const clamp = (key, value) => {
    const [low, high] = BARGE_IN_BOUNDS[key];
    return Math.min(high, Math.max(low, value));
};
/** Clamp, then the nearest 32 ms window that is still inside the bounds. */
const toWindow = (key, ms) => {
    const [low, high] = BARGE_IN_BOUNDS[key];
    const lowest = Math.ceil(low / WINDOW_MS) * WINDOW_MS;
    const highest = Math.floor(high / WINDOW_MS) * WINDOW_MS;
    return Math.min(highest, Math.max(lowest, Math.round(clamp(key, ms) / WINDOW_MS) * WINDOW_MS));
};
export const normalizeBargeInConfig = (raw) => {
    const src = raw && typeof raw === 'object' ? raw : {};
    const d = DEFAULT_BARGE_IN_CONFIG;
    const onset = toWindow('onset_ms', num(src.onset_ms, d.onset_ms));
    const abandon = toWindow('abandon_ms', num(src.abandon_ms, d.abandon_ms));
    // The max window is the only way out of a half-built onset under alternating evidence;
    // below 2 x onset + one window, speech with a single dip can never confirm.
    const maxWindow = Math.max(toWindow('max_window_ms', num(src.max_window_ms, d.max_window_ms)), 2 * onset + WINDOW_MS);
    return {
        probability: Math.round(clamp('probability', num(src.probability, d.probability)) * 1000) / 1000,
        onset_ms: onset,
        abandon_ms: abandon,
        max_window_ms: maxWindow,
        min_level_dbfs: Math.round(clamp('min_level_dbfs', num(src.min_level_dbfs, d.min_level_dbfs)) * 10) / 10,
    };
};
/** The detector's parameters for a config. `rmsFloor` holds the PROBABILITY (historical name). */
export const toDetectorParams = (config) => ({
    rmsFloor: config.probability,
    onsetMs: config.onset_ms,
    abandonMs: config.abandon_ms,
    maxOnsetWindowMs: config.max_window_ms,
});
/** Loudness of a block of samples (-1..1) in dBFS; silence reads as -100. */
export const levelDbfs = (samples, start = 0, length = samples.length) => {
    if (length <= 0)
        return -100;
    let sum = 0;
    for (let i = start; i < start + length; i += 1) {
        const s = samples[i];
        sum += s * s;
    }
    const rms = Math.sqrt(sum / length);
    if (!(rms > 0))
        return -100;
    return Math.max(-100, 20 * Math.log10(rms));
};
